import { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import { Save, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdAutocomplete from '@/components/ui/CpdAutocomplete';
import { useGetFacilities } from '@/api/hooks/facilities/useGetFacilities';
import { FacilityGroupsApi } from '@/api/clients/facility-groups/facility-groups.api';

const FacilityAssignmentSection = ({
  facilityGroup,
  organizationId,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);
  const [initialFacilityIds, setInitialFacilityIds] = useState([]);

  const {
    facilities,
    loading: facilitiesLoading,
    error: facilitiesError,
  } = useGetFacilities({
    organizationId,
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (facilityGroup && facilities.length > 0) {
      const assignedIds = facilities
        .filter((f) => f.facility_group_id === facilityGroup.id)
        .map((f) => f.id);
      setSelectedFacilityIds(assignedIds);
      setInitialFacilityIds(assignedIds);
    }
  }, [facilityGroup, facilities]);

  const facilitiesWithGroupInfo = useMemo(() => {
    return facilities.map((f) => ({
      ...f,
      currentGroupId: f.facility_group_id,
      isReassignment:
        f.facility_group_id && f.facility_group_id !== facilityGroup?.id,
    }));
  }, [facilities, facilityGroup]);

  const selectedFacilities = useMemo(() => {
    return facilitiesWithGroupInfo.filter((f) =>
      selectedFacilityIds.includes(f.id),
    );
  }, [facilitiesWithGroupInfo, selectedFacilityIds]);

  const hasReassignments = selectedFacilities.some((f) => f.isReassignment);

  const hasChanges =
    JSON.stringify([...selectedFacilityIds].sort()) !==
    JSON.stringify([...initialFacilityIds].sort());

  const handleChange = (event, newValue) => {
    setSelectedFacilityIds(newValue.map((f) => f.id));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await FacilityGroupsApi.assignFacilities(
        organizationId,
        facilityGroup.id,
        selectedFacilityIds,
      );
      toast.success(t('facilityGroups.assign.success'));
      setInitialFacilityIds(selectedFacilityIds);
      onUpdate?.();
    } catch {
      toast.error(t('facilityGroups.assign.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedFacilityIds(facilitiesWithGroupInfo.map((f) => f.id));
  };

  const handleClearAll = () => {
    setSelectedFacilityIds([]);
  };

  const renderOption = (props, facility) => (
    <Box component='li' {...props}>
      <Box sx={{ flex: 1 }}>
        <Typography variant='body2'>{facility.name}</Typography>
        {facility.currentGroupId && (
          <Typography variant='caption' color='warning.main'>
            {facility.currentGroupId === facilityGroup?.id
              ? t('facilityGroups.assign.currentlyAssigned')
              : t('facilityGroups.assign.willReassign')}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderTags = (tagValue, getTagProps) =>
    tagValue.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index });
      return (
        <Chip
          key={key}
          variant='outlined'
          label={option.name}
          color={option.isReassignment ? 'warning' : 'primary'}
          size='small'
          {...tagProps}
        />
      );
    });

  if (!facilityGroup) return null;

  return (
    <Box>
      <Typography variant='h6' gutterBottom sx={{ mt: 3, mb: 2 }}>
        {t('facilityGroups.assign.sectionTitle')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {hasReassignments && (
          <Alert severity='warning' icon={<Info />}>
            {t('facilityGroups.assign.reassignWarning')}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={handleSelectAll}
            disabled={
              selectedFacilityIds.length === facilitiesWithGroupInfo.length ||
              loading
            }>
            {t('facilityGroups.assign.selectAll', {
              count: facilitiesWithGroupInfo.length,
            })}
          </Button>
          <Button
            size='small'
            variant='outlined'
            onClick={handleClearAll}
            disabled={selectedFacilityIds.length === 0 || loading}>
            {t('facilityGroups.assign.clearAll')}
          </Button>
        </Box>

        <CpdAutocomplete
          multiple
          options={facilitiesWithGroupInfo}
          value={selectedFacilities}
          onChange={handleChange}
          label={t('facilityGroups.assign.selectFacilities')}
          placeholder={t('facilityGroups.assign.searchPlaceholder')}
          loading={facilitiesLoading}
          apiError={facilitiesError}
          disabled={loading}
          getOptionLabel={(f) => f.name}
          renderOption={renderOption}
          renderTags={renderTags}
        />

        <Typography variant='body2' color='text.secondary'>
          {t('facilityGroups.assign.selectedCount', {
            count: selectedFacilityIds.length,
          })}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <CpdActionButton
            onClick={handleSave}
            loading={loading}
            disabled={!hasChanges}
            startIcon={<Save />}>
            {t('common.buttons.saveChanges')}
          </CpdActionButton>
        </Box>
      </Box>
    </Box>
  );
};

export default FacilityAssignmentSection;
